import BpkBadge, {
  BADGE_TYPES,
} from '@skyscanner/backpack-web/bpk-component-badge';
import BpkText, {
  TEXT_STYLES,
} from '@skyscanner/backpack-web/bpk-component-text';
import { cssModules } from '@skyscanner/backpack-web/bpk-react-utils';

import ImageItem from '../ImageItem';

import GridItem from './GridItem';
import ImageGalleryMap from './ImageGalleryMap';

import type { MEDIA_TYPE_HOTEL_IMAGE } from '@hotels-hotel-detail/utils/hotel-images';
import type { Configs } from '@shared/types/configs';
import type { UserContext } from '@shared/types/user-context';

import STYLES from './MediaGrid.module.scss';

import { useHotelPreviewImages } from '@hotels-hotel-detail/data-access/hooks/useHotelContent';
import { useHotelImagesContext } from '@hotels-hotel-detail/data-access/hooks/useHotelImagesContext';
import {
  VIDEOS_CATEGORY,
  MEDIA_TYPE_HOTEL_DESCRIPTION,
} from '@hotels-hotel-detail/utils/hotel-images';
import { buildDetailsImageGalleryAttributes } from '@hotels-hotel-detail/utils/meaningful-behaviour-data/hotel-details-image-gallery';
import { withConfiguration } from '@shared/utils-frontend/contexts/ConfigurationContext';
import { withUserContext } from '@shared/utils-frontend/contexts/UserContext';

const cls = cssModules(STYLES);
export type MediaType =
  | typeof MEDIA_TYPE_HOTEL_DESCRIPTION
  | typeof MEDIA_TYPE_HOTEL_IMAGE;

type Props = {
  mediaType: MediaType;
  hotelId?: string;
  configs: Configs;
  userContext: UserContext;
};

const MediaGrid = ({ configs, hotelId, mediaType, userContext }: Props) => {
  const { descImagesPreview, heroImagesPreview, imageCount } =
    useHotelPreviewImages();
  const { openGallery } = useHotelImagesContext();

  const isDescriptionMediaType = mediaType === MEDIA_TYPE_HOTEL_DESCRIPTION;

  if (
    (!isDescriptionMediaType && heroImagesPreview.length === 0) ||
    (isDescriptionMediaType && descImagesPreview.length === 0)
  ) {
    return null;
  }

  const nodes = (
    isDescriptionMediaType ? descImagesPreview : heroImagesPreview
  ).map(({ type, url }, imageIndex) => {
    const imageGridMBDProps = buildDetailsImageGalleryAttributes({
      hotelId,
      sortId: imageIndex,
      mediaType,
    });

    return (
      <ImageItem
        key={`${type}_${url}`}
        src={url!}
        imageIndex={imageIndex}
        mbdProps={imageGridMBDProps}
        showVideoPlayOverlay={type === VIDEOS_CATEGORY}
        openGallery={openGallery}
        lazyLoad={isDescriptionMediaType}
      />
    );
  });

  if (
    userContext.enableSEOVariantHotelsDetailsPage &&
    configs.enableImageGallerySEOVariant
  ) {
    nodes.splice(3, 1, <ImageGalleryMap />);
  }
  const moreImagesLen = imageCount - nodes.length;
  const seeMoreMediaButtonText = `+ ${moreImagesLen}`;

  return (
    <div
      className={cls('MediaGrid', `MediaGrid__${mediaType}`)}
      data-test-id="media-grid-section"
    >
      <GridItem imageNumber={nodes.length}>{nodes}</GridItem>
      {moreImagesLen > 0 && !isDescriptionMediaType && (
        <BpkBadge
          type={BADGE_TYPES.inverse}
          className={cls('MediaGrid__imageNumberIndicator')}
        >
          <BpkText textStyle={TEXT_STYLES.footnote}>
            {seeMoreMediaButtonText}
          </BpkText>
        </BpkBadge>
      )}
    </div>
  );
};

export default withUserContext(withConfiguration(MediaGrid));
